<template>
  <div
    class="bed-visualizer-customize bed-visualizer__page"
    :class="[`bed-visualizer-customize--${stepName.toLowerCase()}`]"
  >
    <div class="bed-visualizer__head">
      <div
        class="bed-visualizer__step body-2"
        v-text="$string('bedVisualizer.customize.step', {
          replace: {
            stepCount,
            totalStepCount,
          }
        })"
      />

      <h1
        class="bed-visualizer__title h6"
        v-text="title"
      />

      <p
        v-if="description"
        class="bed-visualizer__description body-2"
        v-text="description"
      />

      <div class="bed-visualizer__actions">
        <template v-if="!filtersOpen">
          <btn
            v-show="state === 'ready'"
            class="bed-visualizer__action bed-visualizer__filter"
            type="button"
            :icon="false"
            :label="$string('bedVisualizer.filters.cta')"
            :modifiers="[
              'center',
              'small',
              'outlined',
            ]"
            @click.prevent="$emit('filter-toggle-clicked')"
          />
        </template>

        <template v-else-if="!isMobile">
          <btn
            class="bed-visualizer__action bed-visualizer__filter--apply"
            type="button"
            :icon="false"
            :label="$string('bedVisualizer.filters.applyFilters')"
            :modifiers="[
              'center',
              'secondary',
              'small',
            ]"
            @click.prevent="$emit('filter-toggle-clicked')"
          />
        </template>
      </div>
    </div>

    <div
      ref="scrollElement"
      class="bed-visualizer__scroll"
    >
      <div
        v-if="filtersOpen"
        class="bed-visualizer__filters"
      >
        <div
          class="
            bed-visualizer__filters-active
            bed-visualizer__filters-facet
          "
        >
          <div
            class="bed-visualizer__filters-active-title h6"
            v-text="activeFiltersText"
          />

          <button
            v-if="appliedFiltersCount > 0"
            class="bed-visualizer__filters-clear button button--text text-link"
            @click.prevent="handleClearAllFilters"
            v-text="$string('bedVisualizer.filters.clearAll')"
          />

          <ul
            v-if="appliedFiltersCount > 0"
            class="bed-visualizer__filters-facet-content"
          >
            <template
              v-for="group in Object.keys(appliedFilters)"
              :key="group"
            >
              <li
                v-for="filter in appliedFilters[group]"
                :key="filter"
                class="bed-visualizer__filters-facet-item"
                @click.prevent="removeFilter(group, filter)"
              >
                <div
                  v-if="group === 'color'"
                  class="bed-visualizer__filters-facet-swatch"
                  :class="getSwatchBorder(filter)"
                  :style="{ backgroundImage: getSwatchImage(filter), backgroundColor: getSwatchColor(filter) }"
                />
                <div
                  class="bed-visualizer__filters-facet-label"
                  v-text="filter"
                />
                <div
                  class="bed-visualizer__filters-facet-close"
                  v-html="$icon('misc.close')"
                />
              </li>
            </template>
          </ul>
        </div>

        <div
          v-for="group in Object.keys(availableFilters)"
          :key="group"
          class="bed-visualizer__filters-facet"
        >
          <div
            class="bed-visualizer__filters-facet-label body-2"
            v-text="group"
          />

          <ul class="bed-visualizer__filters-facet-content">
            <li
              v-for="filter in availableFilters[group]"
              :key="filter"
              class="bed-visualizer__filters-facet-item"
              @click.prevent="applyFilter(group, filter)"
            >
              <div
                v-if="group === 'color'"
                class="bed-visualizer__filters-facet-swatch"
                :class="getSwatchBorder(filter)"
                :style="{ backgroundImage: getSwatchImage(filter), backgroundColor: getSwatchColor(filter) }"
              />
              <div
                class="bed-visualizer__filters-facet-label"
                v-text="filter"
              />
            </li>
          </ul>
        </div>
      </div>

      <div
        v-else
        class="bed-visualizer__products"
        :class="{[$class('grid')]: gridLayout}"
      >
        <div
          v-if="allProductsUnavailable"
          class="bed-visualizer__no-results"
          v-text="`No ${stepName} products are available.`"
        />

        <div
          v-if="errorMessage"
          v-text="errorMessage"
        />

        <template
          v-for="(variant, index) in filteredVariants"
          :key="index"
        >
          <product-chip
            :block-class="'bed-visualizer'"
            :image="getThumbnailImage(variant?.product, variant)"
            :index="index"
            :is-active="isSelectedOption(variant)"
            :loading="!variant"
            :number="getSelectedOptionIndex(variant) + 1"
            :show-number="limit > 1"
            :product-title="variant?.product?.title"
            :variant="variant"
            :grid-layout="gridLayout"
            @click="handleOptionChange(variant?.product, variant)"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Bed visualizer customize page (bed-visualizer-customize)
 * -----------------------------------------------------------------------------
 * Displays contents of bed visualizer customize page.
 *
 * @param {String} activeFlow - Name of active flow.
 * @param {String} collectionHandle - Collection handle.
 * @param {Number} limit - Max number of products that can be selected.
 * @param {String} nextStepName - Name of the next step.
 * @param {String} prevStepName - Name of the previous step.
 * @param {Array} stackOrders - Array of stack orders for visualizer images.
 * @param {Number} stepCount - Number for this page.
 * @param {String} stepName - Name for this page.
 * @param {Number} totalStepCount - Total number of steps.
 */

import { mapActions, mapState } from 'vuex'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'

import Btn from '~global/btn/btn'
import NavBtn from '../nav-btn'
import ProductChip from '../product-chip'

import { queryCollection } from '~/helpers/query'
import { formatProduct, formatBoostSwatch } from '~/helpers/format'
import product from '~/graphql/queries/product.gql'
import { formatVariant } from '../bed-visualizer-helpers'
import { product_metafield_identifiers, variant_metafield_identifiers } from '~/helpers/metafield_identifiers'

export default {
  name: 'BedVisualizerCustomize',

  components: {
    Btn,
    NavBtn,
    ProductChip,
  },

  props: {
    activeFlow: {
      type: String,
      default: '',
    },
    collectionHandle: {
      type: String,
      default: '',
    },
    filtersOpen: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: true,
    },
    limit: {
      type: Number,
      default: 1,
    },
    nextStepName: {
      type: String,
      default: '',
    },
    prevStepName: {
      type: String,
      default: '',
    },
    stackOrders: {
      type: Array,
      default: () => [1, 1],
    },
    stepCount: {
      type: Number,
      default: 0,
    },
    stepName: {
      type: String,
      default: '',
      required: true,
    },
    totalStepCount: {
      type: [Boolean, Number],
      default: false,
    },
  },

  emits: [
    'option-change',
    'prev-page',
    'next-page',
    'filter-toggle-clicked',
  ],

  data() {
    return {
      allProductsUnavailable: false,
      errorMessage: false,
      products: [],
      queue: [],
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      config: (state) => state['bed-visualizer'].config,
    }),

    /**
     * Computed grid layout when step is 4 and up.
     */
    gridLayout() {
      return this.stepCount > 3
    },

    /**
     * Computed selected options for step.
     */
    selectedOptions() {
      return this.config.selectedOptions[this.stepName] || []
    },

    /**
     * Computed title for step.
     */
    title() {
      if (this.filtersOpen) {
        return this.$string('bedVisualizer.filters.title', {
          replace: {
            stepName: this.stepName,
          },
        })
      }

      if (this.limit === 1) {
        return this.$string('bedVisualizer.customize.title', {
          replace: {
            stepName: this.stepName,
          },
        })
      }

      return this.$string('bedVisualizer.customize.multiChoiceTitle', {
        replace: {
          stepName: this.stepName,
          limit: this.limit,
        },
      })
    },

    /**
     * Computed description for step.
     */
    description() {
      const description = `bedVisualizer.customize.step${this.stepCount}Description`
      return this.$string(description) !== 'No string found' ? this.$string(description) : ''
    },

    /**
     * Computed active filters text.
     */
    activeFiltersText() {
      if (this.appliedFiltersCount > 0) {
        return this.$string('bedVisualizer.filters.active', {
          replace: {
            count: this.appliedFiltersCount,
          },
        })

      } else {
        return this.$string('bedVisualizer.filters.activeEmpty')
      }
    },

    /**
     * Computed color filter map.
     * - Maps variant colors to a group as defined in product tags.
     * - Contains all possible colors.
     */
    colorFilterMap() {
      const map = {}

      this.products.forEach(product => {
        const tags = this.getColorTags(product)

        tags.forEach(tag => {
          const [
            ,
            colorName,
            groupName,
          ] = tag.split(':')

          const sanitizedGroupName = groupName.toLowerCase()
          if (!map[sanitizedGroupName]) {
            map[sanitizedGroupName] = []
          }

          if (!map[sanitizedGroupName].includes(colorName)) {
            map[sanitizedGroupName].push(colorName)
          }
        })
      })

      return map
    },

    /**
     * Computed available color filter map.
     * - same as colorFilterMap except only includes colors that exist
     * with the current product selection.
     */
    availableColorFilterMap() {
      const map = {}

      this.globallyFilteredVariants.forEach(variant => {
        if (!variant.filters) {
          return
        }
        const color = variant.filters.color
        if (color) {
          map[color.toLowerCase()] = this.colorFilterMap[color.toLowerCase()]
        }
      })

      return map
    },

    /**
     * Computed available products.
     */
    availableProducts() {
      return this.products.filter(product => product && product.available)
    },

    /**
     * Computed available filters.
     * - Removes applied filters as options.
     */
    availableFilters() {
      const color = Object.keys(this.availableColorFilterMap)
        .filter((color) => !this.appliedFilters?.color?.includes(color))

      return {
        color,
      }
    },

    /**
     * Computed applied filters for this step.
     */
    appliedFilters() {
      const { selectedFilters } = this.config

      if (!selectedFilters) {
        return []
      }

      return selectedFilters[this.stepName] || []
    },

    /**
     * Computed applied filters count.
     */
    appliedFiltersCount() {
      return Object.values(this.appliedFilters).flat().length
    },

    /**
     * Computed flattened variants.
     * - Flattens all variants into a single array.
     * - Filters out unavailable products.
     * - Augments variant object with additional data:
     *  - Each variant has a reference to its own product.
     *  - Color group is added based on tags
     */
    flattenedVariants() {
      const variants = this.availableProducts.map(product => {
        const singleVariant = this.isSingleVariant(product)

        const augmentedVariants = product?.variants?.map(variant => {

          /**
           * Reference the parent product in the variant object for easier use
           * in templates.
           */
          variant.product = product

          /**
           * Build filters object so we know which filters apply to this variant.
           */
          variant.filters = {}


          /**
           * Add the color group to object if applicable.
           * - If there is only one variant and only one color tag, assume the
           * color group applies to the variant.
           */
          if (singleVariant) {
            const colorTags = this.getColorTags(product)

            if (colorTags.length === 1) {
              const group = colorTags[0].split(':')[2]
              variant.filters.color = group
            }

          } else {
            const color = this.getVariantColorGroup(variant)

            if (color) {
              variant.filters.color = color
            }
          }

          return variant
        })

        return augmentedVariants
      })

      return variants.flat()
    },

    /**
     * Computed globally filtered products.
     * - Applies only global filters to products (e.g. size).
     * - These are filters that are applied on every step of the flow as
     * opposed to selectedFilters which are only applied on the current step.
     */
    globallyFilteredVariants() {
      const variants = this.flattenedVariants

      /**
       * If still loading return array with false values to render product-chips
       * in loading state.
       */
      if (this.state === 'loading') {
        return [
          false,
          false,
          false,
          false,
        ]
      }

      /**
       * If error state return empty array to avoid rendering product-chip
       * loading states.
       */
      if (this.state === 'error' || !variants) {
        return []
      }

      const filterNames = Object.keys(this.config.filters)
      const availableVariants = variants.filter(variant => variant.available)

      /**
       * If no filters exist return all available products.
       */
      if (!filterNames.length) {
        return availableVariants
      }

      const filtered = availableVariants.filter((variant) => {
        let matchesFilters = true

        /**
         * Check all filters in config to see if variant matches.
         */
        filterNames.forEach(name => {
          const value = this.getOptionValueByName(variant, name)

          /**
           * If filter name doesn't exist in a variant option don't proceed.
           *
           * This is to avoid hiding products when the filter can't be met
           * (e.g. a pillow can't be size:queen, so we don't want to hide it
           * if size doesn't exist as an option)
           */
          if (!value || this.gridLayout) {
            return
          }

          if (this.config.filters[name].indexOf(value.toLowerCase()) === -1) {
            matchesFilters = false
            return
          }
        })

        return matchesFilters
      })

      return filtered
    },

    /**
     * Computed filtered products.
     */
    filteredVariants() {
      const { selectedFilters } = this.config

      if (!selectedFilters || !selectedFilters[this.stepName]) {
        return this.globallyFilteredVariants
      }

      const stepFilters = selectedFilters[this.stepName]
      const hasValues = Object.values(stepFilters).flat().length
      const filterNames = Object.keys(stepFilters)

      /**
       * Show all products if no filters applied.
       */
      if (!hasValues) {
        return this.globallyFilteredVariants
      }

      const filtered = this.globallyFilteredVariants.filter((variant) => {
        let matchesFilters = false

        /**
         * Check all filters in config to see if variant matches.
         */
        filterNames.forEach(name => {
          if (!variant.filters || !variant.filters[name] || !stepFilters[name]) {
            return
          }

          if (stepFilters[name].includes(variant.filters[name])) {
            matchesFilters = true
          }
        })

        return matchesFilters
      })

      return filtered
    },
  },

  mounted() {
    disableBodyScroll(this.$refs.scrollElement)
    this.getProducts()
  },

  unmounted() {
    clearAllBodyScrollLocks()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      saveConfig: 'bed-visualizer/saveConfig',
      addFilters: 'bed-visualizer/addFilters',
      removeFilters: 'bed-visualizer/removeFilters',
      removeAllFilters: 'bed-visualizer/removeAllFilters',
    }),

    /**
     * Get variant color group.
     * @param {Object} variant - variant object
     * @returns {String}
     */
    getVariantColorGroup(variant) {
      const color = this.getOptionValueByName(variant, 'color').toLowerCase()
      let colorGroup

      Object.keys(this.colorFilterMap).forEach(groupName => {
        if (!Array.isArray(this.colorFilterMap[groupName])) {
          return
        }

        const sanitizedColors = this.colorFilterMap[groupName].map(color =>
          color.toLowerCase(),
        )

        if (sanitizedColors.includes(color)) {
          colorGroup = groupName
        }
      })

      return colorGroup
    },

    /**
     * Get color tags from a product.
     * @returns {Array}
     */
    getColorTags(product) {
      return product?.tags.filter(tag => tag.includes('visualizer-color:'))
    },

    /**
     * Check if a product has a single variant.
     * @param {Object} product - Product object.
     * @returns {Boolean}
     */
    isSingleVariant(product) {
      return product.hasOnlyDefaultVariant || product?.variants.length === 1
    },

    /**
     * Handle clear all filters.
     */
    handleClearAllFilters() {
      this.removeAllFilters({
        step: this.stepName,
      })
    },

    /**
     * Apply filter.
     * @param {String} group - filter group name.
     * @param {String} filter - filter name.
     */
    applyFilter(group, filter) {
      this.addFilters({
        step: this.stepName,
        filters: {
          [group]: filter,
        },
      })
    },

    /**
     * Remove filter.
     * @param {String} group - filter group name.
     * @param {String} filter - filter name.
     */
    removeFilter(group, filter) {
      this.removeFilters({
        step: this.stepName,
        filters: {
          [group]: filter,
        },
      })
    },

    /**
     * Handle option change..
     * @param {Object} product - Product object.
     * @param {Object} variant - Variant object.
     */
    handleOptionChange(product, variant) {
      let newOptions = [...this.selectedOptions]
      const limitReached = this.selectedOptions.length >= this.limit
      const isSelected = this.isSelectedOption(variant)

      /**
       * Remove selected option if currently selected.
       */
      if (isSelected) {
        const optionIndex = this.getSelectedOptionIndex(variant)
        newOptions.splice(optionIndex, 1)
      }

      if (this.limit === 1) {

        /**
         * Remove all selected options automatically if product limit is 1.
         * This will allow the user to select a new option without having
         * to unselect another.
         */
        newOptions = []

      } else if (!isSelected && limitReached) {

        /**
         * Do nothing if limit > 1 and current product isn't selected and limit
         * is reached. This will force the user to unselect an option before
         * they can add a new one.
         */
        return
      }

      if (!isSelected) {
        const newOption = {
          ...formatVariant(product, variant, this.stackOrders),
          collection: this.collectionHandle,
        }

        newOptions.push(newOption)
      }

      this.$emit('option-change', {
        name: this.stepName,
        selectedOptions: newOptions,
      })
    },

    /**
     * Get selected option index.
     * @param {Object} variant - Variant object.
     * @returns {Number}
     */
    getSelectedOptionIndex(variant) {
      if (!variant) {
        return 0
      }

      const optionIndex = this.selectedOptions.findIndex(option =>
        option.id === variant.id,
      )

      return optionIndex
    },

    /**
     * Is selected option.
     * @param {Object} variant - Variant object.
     * @returns {Boolean}
     */
    isSelectedOption(variant) {
      if (!variant) {
        return false
      }

      return this.getSelectedOptionIndex(variant) > -1
    },

    /**
     * Get option value by name.
     * @param {Object} variant - Variant object.
     * @param {String} name - Option name.
     * @returns {String}
     */
    getOptionValueByName(variant, name) {
      const namedOption = variant.selectedOptions.find(
        option => option.name.toLowerCase() === name.toLowerCase(),
      )

      return namedOption ? namedOption.value : ''
    },

    /**
     * Get thumbnail image.
     * @param {Object} product - Product object.
     * @param {Object} variant - Product variant object.
     * @returns {Object|Boolean}
     */
    getThumbnailImage(product, variant) {
      if (!product || !variant || !product?.images || !variant?.image) {
        return false
      }

      return product.images.find(image => image.id === variant.image)
    },

    /**
     * Sanitizes image alt and checks for existence of text.
     * @param {Object} image - Image object.
     * @param {String|RegExp} text - Text to check for.
     * @returns {Boolean}
     */
    altContains(image, text) {
      const sanitizedAlt = image?.alt?.toLowerCase()

      if (!sanitizedAlt) {
        return false
      }

      if (text instanceof RegExp) {
        return text.test(sanitizedAlt)
      }

      return sanitizedAlt.includes(text)
    },

    /**
     * Get most relevant visualizer images. Supports up to 2 images using
     * 'visualizer' and 'visualizer 2' prefixes.
     *
     * Example: A gallery with 'visualizer green' and 'visualizer 2 green' will
     * return an array with both images.
     *
     * @param {Object} product - Product object.
     * @param {Object} variant - Product variant object.
     * @returns {Array}
     */
    getVisualizerImages(product, variant) {
      if (!product.images.length) {
        return []
      }

      const { altContains, getOptionValueByName } = this
      const matchingImages = []

      const color = getOptionValueByName(variant, 'color').toLowerCase()
      const size = getOptionValueByName(variant, 'size').toLowerCase()

      /**
       * Check if image matches color value.
       * @param {Object} image - Image object.
       * @returns {Boolean}
       */
      const matchesColor = (image) => altContains(image, ` ${color}`)

      /**
       * Check if image matches size value.
       * @param {Object} image - Image object.
       * @returns {Boolean}
       */
      const matchesSize = (image) => altContains(image, ` ${size}`)

      /**
       * Get the closest match from a set of visualizer images.
       * Match priority as follows:
       *   1. Matches color AND size
       *   2. Matches color
       *   3. Matches size
       *   4. First 'visualizer' image
       *   5. Return false if no matches
       * @param {Array} images - Images to search.
       * @returns {Boolean}
       */
      const getMatch = images => {
        if (!images.length) {
          return false
        }

        const fullMatch = images.find(image => matchesColor(image) && matchesSize(image))
        const colorMatch = images.find(matchesColor)
        const sizeMatch = images.find(matchesSize)

        return fullMatch || colorMatch || sizeMatch || images[0]
      }

      const primaryImages = product.images.filter(image => altContains(image, /visualizer(?! 2)/))
      const secondaryImages = product.images.filter(image => altContains(image, 'visualizer 2'))
      const primaryImage = getMatch(primaryImages)
      const secondaryImage = getMatch(secondaryImages)

      if (primaryImage) {
        matchingImages.push(primaryImage)
      }

      if (secondaryImage) {
        matchingImages.push(secondaryImage)
      }

      return matchingImages
    },

    /**
     * Get products from two possible sources.
     */
    getProducts() {
      if (this.activeFlow !== 'collections-customize') {
        this.queryProducts()
        return
      }

      this.getCollectionFlowProducts()
    },

    /**
     * Get products in selected collection from saved config.
     */
    getCollectionFlowProducts() {
      const products = this.config.selectedCollection?.products[this.stepName]
      if (!products || !products.available) {
        this.state = 'error'
        this.products = []
        this.allProductsUnavailable = true
        return
      }

      this.products = products.available
      this.state = 'ready'
    },

    /**
     * Get swatch color border.
     * @param {String} value - color value.
     */
    getSwatchBorder(value) {
      return formatBoostSwatch(value, 'border')
    },

    /**
     * Get swatch color based on value key to be used as a fallback.
     * @param {String} value - color value.
     */
    getSwatchColor(value) {
      return formatBoostSwatch(value, 'color')
    },

    /**
     * Get swatch image based on color choice.
     * @param {String} value - color value.
     */
    getSwatchImage(value) {
      return formatBoostSwatch(value, 'image')
    },

    /**
     * Query products from GraphQL API.
     */
    async queryProducts() {
      try {
        this.collection = await queryCollection(this.collectionHandle)
        this.handles = this.collection.products.map(product => product.handle)
        this.queue = this.handles.map((handle) => {
          return cnvs.Query({
            locale: cnvs.store.language,
            query: product,
            variables: {
              country: cnvs.store.country,
              handle,
              identifiers: product_metafield_identifiers,
              variant_identifiers: variant_metafield_identifiers,
            },
          })
        })

        const response = await Promise.allSettled(this.queue)

        const products = response.map((promise) => {
          if (promise.status !== 'fulfilled') {
            return false
          }

          return formatProduct(promise.value)
        }).filter(Boolean)

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        /**
         * If no products returned then error.
         */
        if (!products.length) {
          this.state = 'error'
          this.products = []
          this.allProductsUnavailable = true
          return
        }

        this.products = products
        this.state = 'ready'

      } catch (error) {
        this.state = 'error'
        this.products = []
        this.errorMessage = error
      }
    },
  },
}
</script>

<style lang="scss">
@import './bed-visualizer.customize';
</style>
